// Two pieces of global state, and only two: which country the visitor is buying into, and
// which language they read. Both persist, because re-picking your country on every visit is
// the fastest way to make a portal feel broken.
//
// Country is not cosmetic — it changes every price on the site, so it is a URL-independent
// app-level choice rather than a per-page filter.

import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { translate, COUNTRY_LANG, COUNTRIES, LANGS } from "../i18n/index.js";
import { api } from "../lib/api.js";

const Ctx = createContext(null);
const KEY_COUNTRY = "apex_country";
const KEY_LANG = "apex_lang";

const read = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const write = (k, v) => { try { localStorage.setItem(k, v); } catch { /* private mode */ } };

/** Best guess from the browser before the visitor has chosen. */
function detectCountry() {
  const stored = read(KEY_COUNTRY);
  if (stored && COUNTRIES.some((c) => c.code === stored)) return stored;
  const loc = (navigator.language || "").toLowerCase();
  if (loc.includes("-ch") || loc === "gsw") return "CH";
  if (loc.includes("-fr")) return "FR";
  if (loc.includes("-de") || loc.includes("-at")) return "DE";
  if (loc.includes("-lu")) return "LU";
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (tz === "Europe/Zurich") return "CH";
    if (tz === "Europe/Paris") return "FR";
    if (tz === "Europe/Berlin") return "DE";
    if (tz === "Europe/Luxembourg") return "LU";
  } catch { /* ignore */ }
  return "CH";
}

function detectLang(country) {
  const stored = read(KEY_LANG);
  if (stored && LANGS.some((l) => l.code === stored)) return stored;
  const browser = (navigator.language || "").slice(0, 2).toLowerCase();
  if (LANGS.some((l) => l.code === browser)) return browser;
  return COUNTRY_LANG[country] || "en";
}

export function AppProvider({ children }) {
  const [country, setCountryState] = useState(detectCountry);
  const [lang, setLangState] = useState(() => detectLang(detectCountry()));

  const setCountry = useCallback((c) => {
    setCountryState(c);
    write(KEY_COUNTRY, c);
    // Changing country nudges the language only if the visitor never picked one, so a
    // French speaker buying into Germany is not switched to German mid-session.
    if (!read(KEY_LANG)) {
      const l = COUNTRY_LANG[c];
      if (l) setLangState(l);
    }
  }, []);

  const setLang = useCallback((l) => { setLangState(l); write(KEY_LANG, l); }, []);

  useEffect(() => { document.documentElement.lang = lang; }, [lang]);

  // Who is signed in.
  //
  // `undefined` until the first answer comes back, and the difference matters: rendering the
  // sign-in form while we simply have not asked yet throws a returning customer out of their
  // own session for half a second on every page load.
  const [user, setUser] = useState(undefined);
  // Whether the catalogue is gated at all. The server drops the wall when it cannot send
  // sign-in links, and the app has to draw the same world the API is enforcing rather than
  // keep a form over a door that is standing open.
  const [wall, setWall] = useState(true);
  useEffect(() => {
    let live = true;
    api.me()
      .then((r) => { if (!live) return; setUser(r.user ?? null); setWall(r.wall !== false); })
      .catch(() => live && setUser(null));
    return () => { live = false; };
  }, []);

  // The session lives in an HttpOnly cookie, which this code cannot read and therefore
  // cannot notice expiring. The API saying 401 is the only signal there is, so it is the
  // signal used: any guarded request that comes back unauthorised drops us to signed-out.
  useEffect(() => api.onUnauthorized(() => setUser(null)), []);

  const signIn = useCallback(
    (email, redirect) => api.requestLink({ email, country, lang, redirect }),
    [country, lang],
  );

  const signOut = useCallback(async () => {
    try { await api.logout(); } finally { setUser(null); }
  }, []);

  const value = useMemo(() => {
    const meta = COUNTRIES.find((c) => c.code === country) || COUNTRIES[0];
    return {
      country, setCountry, lang, setLang,
      countryMeta: meta,
      currency: meta.currency,
      countryName: meta.name[lang] || meta.name.en,
      t: (key, vars) => translate(lang, key, vars),
      user, signIn, signOut, wall,
      authReady: user !== undefined,
    };
  }, [country, lang, setCountry, setLang, user, signIn, signOut, wall]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useApp() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useApp must be used inside <AppProvider>");
  return v;
}
